import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

// Components
import { Header } from '@/components/header/Header'
import AgentV2ConversationMenu from '@/agent-v2/conversations/components/AgentV2ConversationMenu'
import AgentV2ConversationDialog from '@/agent-v2/conversations/components/AgentV2ConversationDialog'

// Store
import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { selectAgentV2ConversationFromId } from '@/agent-v2/conversations/store/selectors'
import { selectMatterById } from '@/matter/store/selectors'

// Types
import { ChatListSidebarItem } from '@/chat-common/conversation_list/schema'
import type { BreadcrumbItemType } from '@/components/breadcrumbs/BreadcrumbItem'

// Constants & utils
import { matterPaths, kRouteSegments } from '@/routing/paths'
import { MatterType } from '@/matter/store/schemas'
import { AgentV2ConversationSurface } from '@/agent-v2/store/schemas'
import { isOutlookPrivacyMode } from '@/analytics/privacy/outlook-continuation-privacy'

export default function MatterBreadcrumbsChat() {
  const navigate = useNavigate()
  const { chatFeature, chatId, matterId: urlMatterId } = useParams()

  // Local state for dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteDialogItem, setDeleteDialogItem] = useState<ChatListSidebarItem | null>(null)
  const [changeTitleDialogOpen, setChangeTitleDialogOpen] = useState(false)
  const [changeTitleItem, setChangeTitleItem] = useState<ChatListSidebarItem | null>(null)

  // Dialog handlers
  const openDeleteDialog = (conversationItem: ChatListSidebarItem) => {
    setDeleteDialogItem(conversationItem)
    setDeleteDialogOpen(true)
  }

  const openChangeTitleDialog = (conversationItem: ChatListSidebarItem) => {
    setChangeTitleItem(conversationItem)
    setChangeTitleDialogOpen(true)
  }

  const shouldDisplayBreadcrumbsForFeature: boolean = chatFeature === kRouteSegments.agentV2

  const agentV2Conversation = useAppSelector((state: RootState) => selectAgentV2ConversationFromId(state, { conversationId: chatId }))
  const conversation: ChatListSidebarItem | null = useMemo(() => {
    return agentV2Conversation
      ? {
          id: agentV2Conversation.id,
          title:
            agentV2Conversation.origin_surface === AgentV2ConversationSurface.OUTLOOK && !isOutlookPrivacyMode()
              ? 'Outlook conversation'
              : agentV2Conversation.title,
          matter_id: agentV2Conversation.matter_id,
          modified_on: Date.parse(agentV2Conversation.updated_at),
        }
      : null
  }, [agentV2Conversation])

  // Get display values
  const conversationMatterId = conversation?.matter_id
  const conversationMatter = useAppSelector((state: RootState) => selectMatterById(state, { matterId: conversationMatterId }))
  const urlMatter = useAppSelector((state: RootState) => selectMatterById(state, { matterId: urlMatterId }))
  const title = conversation?.title ?? 'Untitled Conversation'
  // The menu's actions only apply once something's been sent. A message counts once the backend accepts it, since the
  // send (or its recovery) can still fail.
  const hasSentMessage = agentV2Conversation?.has_activity ?? false

  const initialLoadComplete = useAppSelector((state: RootState) => state.mattersState.initialLoadComplete)

  // Build breadcrumb items
  const breadcrumbItems: BreadcrumbItemType[] = useMemo(() => {
    if (!initialLoadComplete) return []

    const items: BreadcrumbItemType[] = []

    // When there's no conversation (while a chat is being created), show the matter name only
    if (!conversation) {
      if (urlMatter) {
        items.push({
          id: `matter-${urlMatter.id}`,
          label: urlMatter.name ?? '',
          onClick: () =>
            navigate(
              urlMatter.type === MatterType.OUTLOOK_WORKSPACE
                ? matterPaths.chat.feature(urlMatter.id, kRouteSegments.agentV2)
                : matterPaths.overview(urlMatter.id)
            ),
        })
      }
      return items
    }

    // Add matter breadcrumb if the conversation belongs to a matter
    if (conversationMatter) {
      items.push({
        id: `matter-${conversationMatter.id}`,
        label: conversationMatter.name,
        onClick: () =>
          navigate(
            conversationMatter.type === MatterType.OUTLOOK_WORKSPACE
              ? matterPaths.chat.feature(conversationMatter.id, kRouteSegments.agentV2)
              : matterPaths.overview(conversationMatter.id)
          ),
      })
    }

    // Add conversation title (always last, no onClick - active item)
    items.push({
      id: `conversation-${conversation.id}`,
      label: title,
    })

    return items
  }, [initialLoadComplete, conversation, conversationMatter, urlMatter, title, navigate])

  if (!shouldDisplayBreadcrumbsForFeature) {
    return null
  }

  return (
    <>
      <AgentV2ConversationDialog
        conversationId={changeTitleItem?.id ?? null}
        kind="rename"
        open={changeTitleDialogOpen}
        onClose={() => {
          setChangeTitleDialogOpen(false)
          setChangeTitleItem(null)
        }}
      />
      <AgentV2ConversationDialog
        conversationId={deleteDialogItem?.id ?? null}
        kind="delete"
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false)
          setDeleteDialogItem(null)
        }}
      />
      <Header
        breadcrumbs={breadcrumbItems}
        endContent={
          conversation && hasSentMessage ? (
            <AgentV2ConversationMenu
              conversationId={conversation.id}
              iconSize="lg"
              iconColor="black"
              onSelectDelete={() => openDeleteDialog(conversation)}
              onSelectRename={() => openChangeTitleDialog(conversation)}
            />
          ) : undefined
        }
      />
    </>
  )
}
